import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  QrCode, ShoppingBag, CreditCard, BarChart3, Users, Gift, Brain, Truck,
  ChefHat, Bell, Clock, Shield, Smartphone, Palette, Globe, Zap,
  ArrowRight, ShoppingCart, Monitor, UserCog, DollarSign, Package,
  LayoutDashboard, Megaphone, Star, Check, MessageCircle, Instagram, Image, TrendingUp
} from "lucide-react";
import { useState } from "react";
import Navbar from "@/components/landing/Navbar";
import FooterSection from "@/components/landing/FooterSection";

const categories = [
  {
    id: "pedidos",
    title: "Cardápio & Pedidos",
    description: "Digitalize toda a experiência de pedidos do seu restaurante.",
    icon: ShoppingBag,
    features: [
      { icon: QrCode, title: "QR Code nas Mesas", desc: "Clientes escaneiam o código na mesa e fazem pedidos direto do celular." },
      { icon: ShoppingBag, title: "Cardápio Digital Completo", desc: "Categorias, variações, adicionais, combos, fotos HD e descrições com IA." },
      { icon: Image, title: "Imagens com IA", desc: "Gere imagens profissionais dos seus produtos automaticamente com inteligência artificial." },
      { icon: Smartphone, title: "Pedido pelo Celular", desc: "Interface mobile-first. Personalização e finalização em poucos toques." },
      { icon: ChefHat, title: "Painel da Cozinha (KDS)", desc: "Tela dedicada com pedidos em tempo real, priorização e status." },
      { icon: Monitor, title: "PDV Completo", desc: "Ponto de venda integrado com cardápio, estoque e relatórios." },
      { icon: LayoutDashboard, title: "Comandas Digitais", desc: "Abra, gerencie e feche comandas diretamente pelo PDV ou cardápio digital." },
    ],
  },
  {
    id: "financeiro",
    title: "Pagamentos & Financeiro",
    description: "Controle total das finanças do seu restaurante.",
    icon: CreditCard,
    features: [
      { icon: CreditCard, title: "Múltiplos Métodos", desc: "PIX, cartão de crédito, débito, dinheiro e MercadoPago." },
      { icon: BarChart3, title: "Dashboard Financeiro", desc: "Faturamento diário, semanal e mensal. Ticket médio e horários de pico." },
      { icon: DollarSign, title: "Contas a Pagar/Receber", desc: "Controle de contas, categorias financeiras e fluxo de caixa." },
      { icon: Clock, title: "Relatório de Custos", desc: "Custo por produto com base nos ingredientes. Margem de lucro real." },
      { icon: Package, title: "Controle de Estoque", desc: "Ingredientes vinculados a produtos com desconto automático por pedido." },
      { icon: Shield, title: "Caixa Registradora", desc: "Abertura e fechamento de caixa com movimentações e conferência." },
    ],
  },
  {
    id: "delivery",
    title: "Delivery & Integrações",
    description: "Seu delivery próprio e integração com marketplaces.",
    icon: Truck,
    features: [
      { icon: Truck, title: "Delivery Próprio", desc: "Raio de entrega, taxas por distância, entregadores e rastreamento." },
      { icon: ShoppingCart, title: "Integração iFood", desc: "Receba pedidos do iFood direto no painel e sincronize o cardápio." },
      { icon: Instagram, title: "Instagram Shop", desc: "Sincronize seu catálogo com o Instagram Shopping e venda pela rede social." },
      { icon: MessageCircle, title: "Catálogo WhatsApp", desc: "Sincronize seus produtos com o catálogo do WhatsApp Business automaticamente." },
      { icon: MessageCircle, title: "WhatsApp Automático", desc: "Notificações de status de pedido e atendimento direto pelo WhatsApp." },
      { icon: Globe, title: "Rastreamento ao Vivo", desc: "Clientes acompanham em tempo real cada etapa do pedido." },
      { icon: Bell, title: "Notificações", desc: "Alertas automáticos para novos pedidos, prontos e entregas." },
      { icon: Clock, title: "Agendamento", desc: "Clientes agendam pedidos para horários futuros." },
    ],
  },
  {
    id: "marketing",
    title: "Marketing & Fidelização",
    description: "Conquiste e mantenha clientes com ferramentas inteligentes.",
    icon: Gift,
    features: [
      { icon: Gift, title: "Programa de Fidelidade", desc: "Pontos, selos, cashback. Programas personalizados que fidelizam." },
      { icon: Users, title: "CRM de Clientes", desc: "Histórico, segmentação, frequência e reativação automática." },
      { icon: Brain, title: "IA de Sugestões", desc: "Recomendações inteligentes e descrições automáticas de produtos." },
      { icon: TrendingUp, title: "Modo Crescimento", desc: "IA analisa seu restaurante e sugere promoções, combos, marketing e reativação de clientes." },
      { icon: Megaphone, title: "Cupons & Promoções", desc: "Crie cupons de desconto e pop-ups promocionais no cardápio." },
      { icon: Palette, title: "Marca Personalizada", desc: "Cores, logo, fontes e layout 100% customizáveis." },
    ],
  },
  {
    id: "gestao",
    title: "Gestão & Equipe",
    description: "Ferramentas operacionais para administrar seu negócio.",
    icon: UserCog,
    features: [
      { icon: UserCog, title: "Gestão de Funcionários", desc: "Cadastro, permissões por papel e acompanhamento de atividades." },
      { icon: Clock, title: "Ponto Digital", desc: "Registro de entrada e saída com cálculo automático de horas." },
      { icon: Star, title: "Avaliações", desc: "Clientes avaliam pedidos com nota de comida e atendimento." },
      { icon: BarChart3, title: "Relatórios Avançados", desc: "Vendas, tendências, produtos mais vendidos e análises detalhadas." },
    ],
  },
];

const Funcionalidades = () => {
  const [activeTab, setActiveTab] = useState("pedidos");
  const activeCat = categories.find(c => c.id === activeTab)!;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* Hero */}
      <section className="pt-28 pb-16">
        <div className="container text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium mb-6">
              +40 Funcionalidades
            </span>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Tudo que seu restaurante precisa,{" "}
              <span className="text-gradient">numa só plataforma</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-10">
              Do cardápio digital ao controle financeiro, do delivery à gestão de equipe.
            </p>
            <div className="flex justify-center gap-4">
              <Button asChild size="lg" className="bg-gradient-primary text-primary-foreground h-14 px-8">
                <Link to="/cadastro">Começar Grátis <ArrowRight className="ml-2 h-5 w-5" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="h-14 px-8">
                <Link to="/lojademo">Ver Demo</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Category tabs */}
      <section className="py-8 border-y border-border sticky top-16 z-40 bg-background/95 backdrop-blur-xl">
        <div className="container">
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none">
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setActiveTab(cat.id)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  activeTab === cat.id
                    ? "bg-primary text-primary-foreground shadow-glow"
                    : "bg-muted text-muted-foreground hover:text-foreground"
                }`}
              >
                <cat.icon className="h-4 w-4" />
                {cat.title}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Active category features */}
      <section className="py-20">
        <div className="container">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <div className="mb-12">
                <h2 className="text-3xl md:text-4xl font-bold mb-3">{activeCat.title}</h2>
                <p className="text-muted-foreground text-lg">{activeCat.description}</p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                {activeCat.features.map((f, i) => (
                  <motion.div
                    key={f.title}
                    className="group p-6 rounded-2xl bg-card border border-border hover:border-primary/30 hover:shadow-glow transition-all"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                  >
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                      <f.icon className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="font-semibold text-lg mb-2">{f.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* Feature highlights */}
      <section className="py-20 bg-muted/30">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Por que escolher o Via Menu?</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {[
              { icon: Zap, title: "Setup em 5 min", desc: "Crie sua conta e comece a vender imediatamente." },
              { icon: Shield, title: "Dados seguros", desc: "Criptografia e isolamento de dados por restaurante." },
              { icon: Globe, title: "100% Online", desc: "Funciona em qualquer dispositivo, sem instalação." },
              { icon: Star, title: "Suporte dedicado", desc: "Chat integrado e central de ajuda completa." },
            ].map((item, i) => (
              <motion.div
                key={i}
                className="text-center p-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="h-7 w-7 text-primary" />
                </div>
                <h3 className="font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="container text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Pronto para digitalizar seu restaurante?</h2>
          <p className="text-muted-foreground text-lg mb-8">Comece grátis e descubra todo o potencial da plataforma.</p>
          <Button asChild size="lg" className="bg-gradient-primary text-primary-foreground h-14 px-10">
            <Link to="/cadastro">Criar Conta Grátis <ArrowRight className="ml-2 h-5 w-5" /></Link>
          </Button>
        </div>
      </section>

      <FooterSection />
    </div>
  );
};

export default Funcionalidades;
